/**
 * Chave de calendário `YYYY-MM-DD` para comparar dias sem depender de timestamp.
 */
export function normalizeCalendarDate(dateStr: string): string {
  const trimmed = dateStr.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return trimmed;
  }
  if (trimmed.includes('T')) {
    return trimmed.split('T')[0];
  }
  return trimmed;
}

export function findDuplicateCalendarDates(dates: string[]): string[] {
  const seen = new Set<string>();
  const duplicates = new Set<string>();

  for (const raw of dates) {
    const key = normalizeCalendarDate(raw);
    if (seen.has(key)) {
      duplicates.add(key);
    } else {
      seen.add(key);
    }
  }

  return [...duplicates];
}

export interface CalendarDatedEntry {
  date: string;
  reason?: string;
}

/**
 * Uma entrada por dia civil; mantém a primeira ocorrência (ex.: reidratação da API).
 */
export function uniqueEntriesByCalendarDay<T extends CalendarDatedEntry>(entries: T[]): T[] {
  const byDay = new Map<string, T>();

  for (const entry of entries) {
    const key = normalizeCalendarDate(entry.date);
    if (!byDay.has(key)) {
      byDay.set(key, { ...entry, date: key } as T);
    }
  }

  return [...byDay.values()].sort((a, b) => a.date.localeCompare(b.date));
}

/** Data local sem deslocamento de fuso (entrada `YYYY-MM-DD` ou ISO). */
export function parseCalendarDateLocal(dateStr: string): Date {
  const key = normalizeCalendarDate(dateStr);
  const [year, month, day] = key.split('-').map(Number);
  return new Date(year, month - 1, day);
}

export function calendarMonthKey(dateStr: string): string {
  return normalizeCalendarDate(dateStr).slice(0, 7);
}

export interface CalendarMonthGroup<T> {
  monthKey: string;
  label: string;
  entries: T[];
}

const monthYearFormatter = new Intl.DateTimeFormat('pt-BR', {
  month: 'long',
  year: 'numeric',
});

function formatMonthYearLabel(monthKey: string): string {
  const [year, month] = monthKey.split('-').map(Number);
  const label = monthYearFormatter.format(new Date(year, month - 1, 1));
  return label.charAt(0).toUpperCase() + label.slice(1);
}

export function groupByCalendarMonth<T extends CalendarDatedEntry>(
  entries: T[]
): CalendarMonthGroup<T>[] {
  const sorted = [...entries].sort((a, b) =>
    normalizeCalendarDate(a.date).localeCompare(normalizeCalendarDate(b.date))
  );

  const groups = new Map<string, T[]>();

  for (const entry of sorted) {
    const key = calendarMonthKey(entry.date);
    const bucket = groups.get(key);
    if (bucket) {
      bucket.push(entry);
    } else {
      groups.set(key, [entry]);
    }
  }

  return [...groups.entries()].map(([monthKey, monthEntries]) => ({
    monthKey,
    label: formatMonthYearLabel(monthKey),
    entries: monthEntries,
  }));
}
